import React from "react";
import { ToolButton, Tooltip } from "@bst/react-ui";
import { useTranslation } from 'react-i18next';

import { MobileSearchButton } from './MobileSearchButton';
import { PAGE_HEADER_ID } from "../../EmbeddedReport/utils/embeddingConfig";

/**
 *
 * @param id - Id for Page or section title
 * @param title - Page or section title
 * @param hasSearch - Boolean used to show search box
 * @param hasSearchNotification - Boolean used to show the indicator when search has a value
 * @param hasFilter - Boolean used to show filter button
 * @param filterSearch - JSX Element for searching for a project
 * @param views - JSX element for changing project views
 * @param sort - JSX element for sorting projects
 * @param hasAddButton - Boolean used to show Add button
 * @param addButtonHandler - Function to handler onClik addButton
 * @param handleShow -Function handler for showing/hiding filter
 * @param handleSearchShow - Function handler for showing/hiding search
 *
 */
interface ProjectHeaderProps {
  id?: string;
  title?: string;
  hasSearch?: boolean;
  hasSearchNotification?: boolean;
  hasFilterNotification?: boolean;
  hasFilter?: boolean;
  filterSearch?: JSX.Element;
  views?: JSX.Element;
  sort?: JSX.Element;
  hasAddButton?: boolean;
  hasTitle?: boolean;
  addButtonHandler?: () => any;
  handleShow?: () => void;
  handleSearchShow?: () => void;
  className?: string;
  children?: React.ReactNode;
  disableFilter?: boolean;
  filterShow?: boolean;
}

/**
 *
 * Render project header
 *
 * @param id - Id for Page or section title
 * @param title - Page or section title
 * @param hasSearch - Boolean used to show search box
 * @param hasSearchNotification - Boolean used to show the indicator when search has a value
 * @param hasFilterNotification - Boolean used to show the indicator when filter has a value
 * @param hasFilter - Boolean used to show filter button
 * @param filterSearch - JSX Element for searching for a project
 * @param views - JSX element for changing project views
 * @param sort - JSX element for sorting projects
 * @param hasAddButton - Boolean used to show Add button
 * @param addButtonHandler - Function to handler onClik addButton
 * @param handleShow -Function handler for showing/hiding filter
 * @param handleSearchShow - Function handler for showing/hiding search
 * @param className - Custom class name
 *
 */
const ProjectHeader = ({
  id,
  title,
  hasSearch,
  hasSearchNotification,
  hasFilterNotification,
  filterSearch,
  views,
  sort,
  hasFilter,
  hasAddButton,
  hasTitle,
  addButtonHandler,
  handleShow,
  handleSearchShow,
  className = "",
  disableFilter,
  filterShow
}: ProjectHeaderProps) => {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <header
        className={`l-subheader l-subheader--home l-subheader--projectheader ${className}`}
        data-testid="subheader"
        id={id || PAGE_HEADER_ID}
      >
        <div className="l-subheader__left">
          {hasFilter && (
            <div className="button-group padding-right">
              <Tooltip content={t("filterTitle")}>
                <ToolButton
                  data-testid="filter-toggle-button"
                  icon="icon-filter"
                  onClick={handleShow}
                  isActive={filterShow}
                  disabled={disableFilter}
                  hasNotification={hasFilterNotification}
                />
              </Tooltip>
              <>{views}</>
            </div>
          )}
          {hasTitle && <h1 className="heading-xxl">{title}</h1>}
        </div>
        <div className="l-subheader__right">
          <div className="button-group">
            {hasSearch && (
              <>
                <div className="u-hide-sm-down filter-search__div__desktop">{filterSearch}</div>
                <MobileSearchButton
                  hasAddButton={hasAddButton}
                  disableFilter={disableFilter && !hasSearchNotification}
                  hasSearchNotification={hasSearchNotification}
                  handleSearchShow={handleSearchShow}
                />
                <div className="global-utilities__item">
                  <>{sort}</>
                </div>
              </>
            )}
            {hasAddButton && (
              <div className="global-utilities__item">
                <Tooltip content={t("userManagementPage.addUser_title")}>
                  <ToolButton
                    data-testid="add-button"
                    icon="icon-add"
                    onClick={addButtonHandler}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </header>
    </React.Fragment>
  );
};

export default ProjectHeader;
